import { Toaster as Sonner, toast as sonnerToast } from 'sonner';
import { BellDot, CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { useCompact } from './hooks';
import { useTheme } from './theme';

export type ToastTone = 'info' | 'success' | 'warning' | 'error' | 'booking';

export interface ToastOptions {
  tone?: ToastTone;
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  /** ms; por defecto según el tono (info/success 6 s, warning 8 s, booking 10 s, error no se va sola). */
  duration?: number;
  id?: string | number;
}

const DURATIONS: Record<ToastTone, number> = {
  info: 6000,
  success: 6000,
  warning: 8000,
  error: Number.POSITIVE_INFINITY,
  booking: 10000,
};

// El toast va invertido (fondo --color-ink): los íconos usan los tokens del esquema opuesto (≥ 3:1).
const ICONS = {
  info: <Info size={18} strokeWidth={1.75} className="text-[var(--color-toast-muted)]" aria-hidden />,
  success: <CircleCheck size={18} strokeWidth={1.75} className="text-[var(--color-toast-good)]" aria-hidden />,
  warning: <TriangleAlert size={18} strokeWidth={1.75} className="text-[var(--color-toast-warn)]" aria-hidden />,
  error: <CircleAlert size={18} strokeWidth={1.75} className="text-[var(--color-toast-bad)]" aria-hidden />,
  booking: <BellDot size={18} strokeWidth={1.75} className="text-[var(--color-toast-accent)]" aria-hidden />,
} as const;

/**
 * Muestra un toast (§6.21). Éxitos en pasado y con el efecto: "Cobro registrado: $ 12.500".
 * Devuelve el id (para `dismissToast`).
 */
export function showToast({ tone = 'info', title, description, action, duration, id }: ToastOptions) {
  const fn =
    tone === 'error'
      ? sonnerToast.error
      : tone === 'warning'
        ? sonnerToast.warning
        : tone === 'success'
          ? sonnerToast.success
          : sonnerToast.info;
  // Errores (§6.21): `role="alert"` (se anuncian enseguida) con título y detalle juntos, y botón "Cerrar".
  const content =
    tone === 'error' ? (
      <span role="alert" className="flex flex-col">
        <span>{title}</span>
        {description ? <span className="mt-0.5 text-caption font-normal opacity-80">{description}</span> : null}
      </span>
    ) : (
      title
    );
  return fn(content, {
    id,
    description: tone === 'error' ? undefined : description,
    duration: duration ?? DURATIONS[tone],
    icon: ICONS[tone],
    action: action ? { label: action.label, onClick: action.onClick } : undefined,
    closeButton: tone === 'error',
  });
}

/** Atajos. */
export const toast = {
  success: (title: string, o?: Omit<ToastOptions, 'title' | 'tone'>) => showToast({ ...o, title, tone: 'success' }),
  info: (title: string, o?: Omit<ToastOptions, 'title' | 'tone'>) => showToast({ ...o, title, tone: 'info' }),
  warning: (title: string, o?: Omit<ToastOptions, 'title' | 'tone'>) => showToast({ ...o, title, tone: 'warning' }),
  error: (title: string, o?: Omit<ToastOptions, 'title' | 'tone'>) => showToast({ ...o, title, tone: 'error' }),
  booking: (title: string, o?: Omit<ToastOptions, 'title' | 'tone'>) => showToast({ ...o, title, tone: 'booking' }),
  /** "Copiado" (2,5 s). */
  copied: (title = 'Copiado') => showToast({ title, tone: 'success', duration: 2500 }),
  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
};

export const dismissToast = (id?: string | number) => sonnerToast.dismiss(id);

/**
 * Montar una vez en la raíz. Escritorio: abajo a la derecha (corrido a la izquierda de una hoja abierta);
 * teléfono: centrado abajo (sobre la barra inferior). Máximo 3 visibles.
 */
export function Toaster({ mobileOffset = 72 }: { mobileOffset?: number }) {
  const compact = useCompact();
  const { resolved } = useTheme();
  return (
    <Sonner
      theme={resolved}
      position={compact ? 'bottom-center' : 'bottom-right'}
      visibleToasts={3}
      gap={8}
      offset={{ bottom: 24, right: 24 }}
      mobileOffset={{ bottom: mobileOffset }}
      containerAriaLabel="Avisos"
      className="atte-toaster"
      toastOptions={{
        unstyled: true,
        closeButtonAriaLabel: 'Cerrar',
        classNames: {
          toast:
            'group flex w-[min(420px,calc(100vw-32px))] items-start gap-3 rounded-md bg-ink px-4 py-3 text-bg shadow-float [&_[data-icon]]:mt-px',
          title: 'text-body font-medium',
          description: 'mt-0.5 text-caption !text-bg opacity-80',
          content: 'flex min-w-0 flex-1 flex-col',
          icon: 'mt-0.5 shrink-0',
          actionButton:
            'ml-auto shrink-0 self-center rounded-sm px-2 py-1 text-label font-medium text-bg underline-offset-4 hover:underline',
          cancelButton: 'shrink-0 self-center rounded-sm px-2 py-1 text-label text-bg/80 hover:text-bg',
          closeButton:
            '!static order-last ml-1 !size-6 shrink-0 !transform-none self-start !border-0 !bg-transparent !text-bg/70 hover:!text-bg',
          success: '[&_[data-icon]]:text-[var(--color-toast-good)]',
          warning: '[&_[data-icon]]:text-[var(--color-toast-warn)]',
          error: '[&_[data-icon]]:text-[var(--color-toast-bad)]',
          info: '',
        },
      }}
    />
  );
}
